import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axiosInstance from "../../utils/axiosConfig";
import { useTranslation } from "react-i18next";

function InventoryList() {
  const { t } = useTranslation();
  const [materials, setMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [materialTypeFilter, setMaterialTypeFilter] = useState("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [imageDialog, setImageDialog] = useState({ open: false, imageUrl: "" });
  const [newMaterial, setNewMaterial] = useState({
    id: null,
    name: "",
    code: "",
    material_type: "",
    quantity: "",
    minimum_quantity: "",
    description: "",
    photo: null,
  });
  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const { data } = await axiosInstance.get("/materials");
      setMaterials(data);
    } catch (error) {
      console.error("Error fetching materials:", error);
    }
  };

  const handleEditMaterial = (material) => {
    setNewMaterial({
      ...material,
      photo: null,
    });
    setOpenDialog(true);
  };

  const handleSaveMaterial = async () => {
    try {
      const formData = new FormData();
      Object.keys(newMaterial).forEach((key) => {
        if (key !== "photo" || (key === "photo" && newMaterial.photo)) {
          formData.append(`material[${key}]`, newMaterial[key]);
        }
      });

      const url = newMaterial.id
        ? `/materials/${newMaterial.id}`
        : `/materials`;
      const method = newMaterial.id ? "PATCH" : "POST";

      await axiosInstance({
        method: method,
        url: url,
        data: formData,
      });

      setOpenDialog(false);
      fetchMaterials();
      setNewMaterial({
        id: null,
        name: "",
        code: "",
        material_type: "",
        quantity: "",
        minimum_quantity: "",
        description: "",
        photo: null,
      });
    } catch (error) {
      console.error("Error saving material:", error);
    }
  };

  // Opciones únicas de tipo de material
  const materialTypeOptions = useMemo(() => {
    const types = Array.from(new Set(materials.map((m) => m.material_type)));
    return [
      { value: "all", label: t("allTypes") || "All Types" },
      ...types.map((type) => ({ value: type, label: type })),
    ];
  }, [materials, t]);

  // Filtrado combinado: por nombre, ubicación y tipo
  const filteredData = useMemo(() => {
    return materials.filter((material) => {
      const matchesName = material.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesLocation =
        !locationSearchTerm ||
        (material.description &&
          material.description.toLowerCase().includes(locationSearchTerm.toLowerCase()));
      const matchesType =
        materialTypeFilter === "all" ||
        material.material_type === materialTypeFilter;
      return matchesName && matchesLocation && matchesType;
    });
  }, [materials, searchTerm, locationSearchTerm, materialTypeFilter]);

  // Paginación: obtener la porción de datos a mostrar
  const paginatedData = useMemo(() => {
    return filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder={t("searchMaterials")}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder={t("location") || "Search by Location"}
            variant="outlined"
            size="small"
            value={locationSearchTerm}
            onChange={(e) => {
              setLocationSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label={t("filterByType") || "Filter by Type"}
            variant="outlined"
            size="small"
            value={materialTypeFilter}
            onChange={(e) => {
              setMaterialTypeFilter(e.target.value);
              setPage(0);
            }}
          >
            {materialTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            {t("addMaterial")}
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("code")}</TableCell>
              <TableCell>{t("name")}</TableCell>
              <TableCell>{t("type")}</TableCell>
              <TableCell>{t("quantity")}</TableCell>
              <TableCell>{t("minimum_quantity")}</TableCell>
              <TableCell>{t("location")}</TableCell>
              <TableCell>{t("photo")}</TableCell>
              <TableCell>{t("actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.code}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.material_type}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{item.minimum_quantity}</TableCell>
                <TableCell>{item.description}</TableCell>
                <TableCell>
                  {item.photo_url ? (
                    <img
                      src={item.photo_url}
                      alt="Material"
                      style={{ width: 50, height: 50, cursor: "pointer" }}
                      onClick={() =>
                        setImageDialog({ open: true, imageUrl: item.photo_url })
                      }
                    />
                  ) : (
                    <p>{t("No Image")}</p>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleEditMaterial(item)}
                  >
                    <EditIcon />
                  </IconButton>
                  {item.photo_url && (
                    <IconButton
                      color="secondary"
                      onClick={() =>
                        setImageDialog({ open: true, imageUrl: item.photo_url })
                      }
                    >
                      <VisibilityIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* Diálogo para agregar/editar material */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {newMaterial.id ? t("editMaterial") : t("addNewMaterial")}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t("code")}
            value={newMaterial.code}
            onChange={(e) =>
              setNewMaterial({ ...newMaterial, code: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("name")}
            value={newMaterial.name}
            onChange={(e) =>
              setNewMaterial({ ...newMaterial, name: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("type")}
            value={newMaterial.material_type}
            onChange={(e) =>
              setNewMaterial({ ...newMaterial, material_type: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("quantity")}
            type="number"
            value={newMaterial.quantity}
            onChange={(e) =>
              setNewMaterial({ ...newMaterial, quantity: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("minimum_quantity")}
            type="number"
            value={newMaterial.minimum_quantity}
            onChange={(e) =>
              setNewMaterial({
                ...newMaterial,
                minimum_quantity: e.target.value,
              })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("location")}
            multiline
            rows={4}
            value={newMaterial.description}
            onChange={(e) =>
              setNewMaterial({ ...newMaterial, description: e.target.value })
            }
            margin="normal"
          />
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              setNewMaterial({ ...newMaterial, photo: e.target.files[0] })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t("cancel")}</Button>
          <Button onClick={handleSaveMaterial} variant="contained">
            {newMaterial.id ? t("update") : t("create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para visualizar la imagen */}
      <Dialog
        open={imageDialog.open}
        onClose={() => setImageDialog({ open: false, imageUrl: "" })}
      >
        <DialogTitle>{t("photo")}</DialogTitle>
        <DialogContent>
          <img
            src={imageDialog.imageUrl}
            alt="Material"
            style={{ width: "100%" }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default InventoryList;
